/*
 * Pure helpers for the WHG Docs panel inside Word. No Office.js here, so
 * everything is unit-testable.
 *
 * Formstack tag grammar (what the merge engine and the e-sign stamper read):
 *   field        {$Client Full Name}
 *   conditional  {if $Marital_Status == "Married"} … {elseif …} … {else} … {/if}
 *   signature    [sig|req|signer1]  [date|req|signer1]  [initial|req|signer2]  [fullname|req|signer1]
 */

export const FIELD_TAG_RE = /\{\$[^{}]+\}/g;
export const CONDITIONAL_TAG_RE = /\{\/?(?:if|elseif|else)\b[^{}]*\}/gi;
export const SIGNATURE_TAG_RE = /\[(?:sig|date|initial|text|check|fullname)\|(?:req|noreq)\|signer\d+(?:\|[^\]|]*)?\]/gi;

export const TAG_KIND = {
  field: { label: "Field", highlightKey: "fields" },
  signature: { label: "Signature", highlightKey: "signatures" },
  conditional: { label: "Condition", highlightKey: "conditionals" },
};

export const classifyTag = (text) => {
  const t = (text || "").trim();
  if (/^\{\$/.test(t)) return "field";
  if (/^\[(sig|date|initial|text|check|fullname)\|/i.test(t)) return "signature";
  if (/^\{\/?(if|elseif|else)\b/i.test(t)) return "conditional";
  return null;
};

// Every tag string found in a block of text, in order, duplicates kept.
export const findTags = (text) => {
  const found = [];
  const source = text || "";
  for (const re of [FIELD_TAG_RE, CONDITIONAL_TAG_RE, SIGNATURE_TAG_RE]) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(source)) !== null) found.push({ text: m[0], index: m.index, kind: classifyTag(m[0]) });
  }
  return found.sort((a, b) => a.index - b.index);
};

export const summarizeTags = (tags) => {
  const counts = { field: 0, signature: 0, conditional: 0 };
  const signers = new Set();
  (tags || []).forEach((t) => {
    if (t.kind) counts[t.kind] += 1;
    if (t.kind === "signature") {
      const m = /signer(\d+)/i.exec(t.text);
      if (m) signers.add(Number(m[1]));
    }
  });
  return { ...counts, signers: [...signers].sort((a, b) => a - b) };
};

export const fieldTag = (name) => `{$${(name || "").trim()}}`;

// Formstack: letters, digits, spaces and underscores; must not start with a digit.
export const cleanFieldName = (raw) => {
  const cleaned = (raw || "")
    .replace(/[{}$]/g, "")
    .replace(/[^A-Za-z0-9 _]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return /^\d/.test(cleaned) ? `Field ${cleaned}` : cleaned;
};

export const signatureTag = (type, signer, { required = true } = {}) =>
  `[${type}|${required ? "req" : "noreq"}|signer${signer}]`;

export const filenameFromUrl = (url) => {
  if (!url) return "";
  const noQuery = String(url).split("?")[0].split("#")[0];
  const last = noQuery.split(/[\\/]/).pop() || "";
  try {
    return decodeURIComponent(last);
  } catch (e) {
    return last;
  }
};

export const formstackIdFromFilename = (filename) => {
  const m = /\[([^[\]]+)\]\.docx$/i.exec((filename || "").trim());
  return m ? m[1].trim() : "";
};

export const filterFields = (fields, query) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return fields || [];
  return (fields || []).filter(
    (f) =>
      (f.field_name || "").toLowerCase().includes(q) ||
      (f.field_label || "").toLowerCase().includes(q) ||
      (f.sf_field_name || "").toLowerCase().includes(q)
  );
};
